import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ArrowRightIcon, UsersIcon, CalendarIcon, MessageSquareIcon, CreditCardIcon, BarChart3Icon, Share2Icon, HistoryIcon, LayoutDashboardIcon, ZapIcon, TargetIcon, StarIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
export function CrmBusinessToolsPage() {
  return <div className="bg-viridia-black min-h-screen" data-id="element-858">
      <SEOHead title="CRM & Business Tools | All-in-One Management | Viridia Analytics" description="Run your local service business from one place. Centralize leads, conversations, appointments, invoices, and follow-up with our comprehensive CRM system." canonical="/crm-business-tools" keywords="CRM for local business, business management tools, lead tracking software, automated invoicing, appointment scheduling software, Phoenix business tools" data-id="element-859" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden min-h-[90vh] flex items-center" data-id="element-860">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-861">
          <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-viridia/10 blur-[120px] animate-pulse-slow" data-id="element-862"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-863">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-864">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-865">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} data-id="element-866">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 border border-viridia/20 text-viridia text-sm font-semibold tracking-wide mb-6" data-id="element-867">
                  CRM & BUSINESS TOOLS
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-868">
                  Run Your Entire Business From{' '}
                  <span className="text-viridia" data-id="element-869">One Place</span>
                </h1>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-870">
                  Stop jumping between disconnected apps. Manage leads, customer
                  conversations, bookings, invoicing, and revenue visibility
                  from a single, centralized command center.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-871">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300 shadow-[0_0_20px_rgba(16,185,129,0.2)]" data-id="element-872">
                    Book a Strategy Call
                    <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-873" />
                  </Link>
                  <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-874">
                    View Packages
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Hero Visual - Command Center */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="relative hidden lg:block h-[500px]" data-id="element-875">
              <div className="absolute inset-0 bg-gradient-to-tr from-viridia-surface to-gray-900 rounded-2xl border border-gray-800 shadow-2xl overflow-hidden p-6" data-id="element-876">
                {/* Header */}
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-800" data-id="element-877">
                  <div className="flex gap-2" data-id="element-878">
                    <div className="w-3 h-3 rounded-full bg-red-500/50" data-id="element-879"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500/50" data-id="element-880"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500/50" data-id="element-881"></div>
                  </div>
                  <div className="text-sm font-medium text-gray-400" data-id="element-882">
                    Business Command Center
                  </div>
                </div>

                {/* Modular Panels */}
                <div className="grid grid-cols-3 gap-4 h-[calc(100%-4rem)]" data-id="element-883">
                  {/* Left Col - Pipeline */}
                  <div className="col-span-1 space-y-4" data-id="element-884">
                    <div className="bg-viridia-black/50 rounded-xl p-4 border border-gray-800/50 h-1/2" data-id="element-885">
                      <div className="text-xs text-gray-500 mb-3 uppercase tracking-wider font-bold" data-id="element-886">
                        Active Pipeline
                      </div>
                      <div className="space-y-2" data-id="element-887">
                        <div className="h-8 bg-blue-500/10 rounded border border-blue-500/20 flex items-center px-3" data-id="element-888">
                          <span className="text-xs text-blue-400" data-id="element-889">
                            New Leads (4)
                          </span>
                        </div>
                        <div className="h-8 bg-yellow-500/10 rounded border border-yellow-500/20 flex items-center px-3" data-id="element-890">
                          <span className="text-xs text-yellow-400" data-id="element-891">
                            Estimates Sent (2)
                          </span>
                        </div>
                        <div className="h-8 bg-viridia/10 rounded border border-viridia/20 flex items-center px-3" data-id="element-892">
                          <span className="text-xs text-viridia" data-id="element-893">
                            Jobs Won (5)
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-viridia-black/50 rounded-xl p-4 border border-gray-800/50 h-1/2" data-id="element-894">
                      <div className="text-xs text-gray-500 mb-3 uppercase tracking-wider font-bold" data-id="element-895">
                        Recent Comms
                      </div>
                      <div className="space-y-3" data-id="element-896">
                        <div className="flex gap-2 items-center" data-id="element-897">
                          <div className="w-6 h-6 rounded-full bg-gray-800 flex-shrink-0" data-id="element-898"></div>
                          <div className="h-2 bg-gray-700 rounded w-full" data-id="element-899"></div>
                        </div>
                        <div className="flex gap-2 items-center" data-id="element-900">
                          <div className="w-6 h-6 rounded-full bg-gray-800 flex-shrink-0" data-id="element-901"></div>
                          <div className="h-2 bg-gray-700 rounded w-3/4" data-id="element-902"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Middle Col - Calendar & Revenue */}
                  <div className="col-span-2 space-y-4" data-id="element-903">
                    <div className="bg-viridia-black/50 rounded-xl p-4 border border-gray-800/50 h-2/3 relative overflow-hidden" data-id="element-904">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-viridia/5 rounded-full blur-2xl" data-id="element-905"></div>
                      <div className="text-xs text-gray-500 mb-4 uppercase tracking-wider font-bold" data-id="element-906">
                        Revenue Snapshot
                      </div>
                      <div className="text-3xl font-bold text-white mb-1" data-id="element-907">
                        $24,850
                      </div>
                      <div className="text-xs text-viridia flex items-center mb-6" data-id="element-908">
                        <TrendingUpIcon className="w-3 h-3 mr-1" data-id="element-909" /> +12% this
                        month
                      </div>
                      {/* Fake Chart */}
                      <div className="flex items-end gap-2 h-24 mt-auto" data-id="element-910">
                        {[40, 60, 45, 80, 65, 90, 75].map((h, i) => <motion.div key={i} initial={{
                        height: 0
                      }} animate={{
                        height: `${h}%`
                      }} transition={{
                        duration: 1,
                        delay: 0.5 + i * 0.1
                      }} className="flex-1 bg-gradient-to-t from-viridia/20 to-viridia/60 rounded-t-sm" data-id="element-911"></motion.div>)}
                      </div>
                    </div>
                    <div className="bg-viridia-black/50 rounded-xl p-4 border border-gray-800/50 h-1/3 flex items-center justify-between" data-id="element-912">
                      <div data-id="element-913">
                        <div className="text-xs text-gray-500 mb-1 uppercase tracking-wider font-bold" data-id="element-914">
                          Next Appointment
                        </div>
                        <div className="text-sm font-medium text-white" data-id="element-915">
                          AC Repair Estimate
                        </div>
                        <div className="text-xs text-gray-400" data-id="element-916">
                          Today, 2:00 PM
                        </div>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-viridia/10 flex items-center justify-center" data-id="element-917">
                        <CalendarIcon className="w-5 h-5 text-viridia" data-id="element-918" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-919">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-920">
          <ScrollReveal data-id="element-921">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-922">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-923">
                The Cost of Scattered Tools
              </h2>
              <p className="text-lg text-gray-400" data-id="element-924">
                Sticky notes, call logs, personal inboxes, text threads, and
                spreadsheets. When your business runs on disconnected tools,
                leads slip through the cracks and admin work eats your day.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-925">
            <ScrollReveal delay={0.1} data-id="element-926">
              <div className="bg-red-900/10 border border-red-900/30 rounded-2xl p-8 relative overflow-hidden" data-id="element-927">
                <div className="absolute top-0 right-0 bg-red-500/20 text-red-400 text-xs font-bold px-3 py-1 rounded-bl-lg" data-id="element-928">
                  THE OLD WAY
                </div>
                <h3 className="text-xl font-bold text-gray-300 mb-6" data-id="element-929">
                  Fragmented Chaos
                </h3>
                <ul className="space-y-4" data-id="element-930">
                  {['Missed follow-ups because a text got buried', 'No visibility into where a lead is in the pipeline', 'Wasted hours jumping between 5 different apps', 'Confusion over unpaid invoices and estimates'].map((item, i) => <li key={i} className="flex items-start text-gray-400" data-id="element-931">
                      <span className="text-red-400 mr-3" data-id="element-932">✕</span> {item}
                    </li>)}
                </ul>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-933">
              <div className="bg-viridia/5 border border-viridia/20 rounded-2xl p-8 relative overflow-hidden shadow-[0_0_30px_rgba(16,185,129,0.05)]" data-id="element-934">
                <div className="absolute top-0 right-0 bg-viridia text-viridia-black text-xs font-bold px-3 py-1 rounded-bl-lg" data-id="element-935">
                  THE VIRIDIA WAY
                </div>
                <h3 className="text-xl font-bold text-white mb-6" data-id="element-936">
                  One Connected System
                </h3>
                <ul className="space-y-4" data-id="element-937">
                  {['Every conversation (text, email, call) in one thread', 'Clear visual pipeline of every opportunity', 'Automated follow-ups that happen while you work', 'Instant visibility into revenue and booked jobs'].map((item, i) => <li key={i} className="flex items-start text-gray-300" data-id="element-938">
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0" data-id="element-939" />{' '}
                      {item}
                    </li>)}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CORE CAPABILITIES */}
      <section className="py-24 bg-viridia-black" data-id="element-940">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-941">
          <ScrollReveal data-id="element-942">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-943">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-944">
                Everything You Need to Operate
              </h2>
              <p className="text-lg text-gray-400" data-id="element-945">
                Not just a contact list. A complete operating system designed to
                help busy business owners stay organized and close more work.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-946">
            {[{
            icon: <UsersIcon className="w-6 h-6" data-id="element-947" />,
            title: 'Contact Management',
            desc: 'Keep every lead and customer record organized with tags, notes, and full history.'
          }, {
            icon: <LayoutDashboardIcon className="w-6 h-6" data-id="element-948" />,
            title: 'Sales Pipeline',
            desc: 'Visually track every opportunity from new lead to estimate sent to job won.'
          }, {
            icon: <CalendarIcon className="w-6 h-6" data-id="element-949" />,
            title: 'Smart Scheduling',
            desc: 'Let customers book directly on your calendar without the back-and-forth emails.'
          }, {
            icon: <MessageSquareIcon className="w-6 h-6" data-id="element-950" />,
            title: 'Unified Inbox',
            desc: 'Texts, emails, calls, and social DMs all flow into one single conversation thread.'
          }, {
            icon: <CreditCardIcon className="w-6 h-6" data-id="element-951" />,
            title: 'Invoicing & Payments',
            desc: 'Send professional estimates and text-to-pay links that get you paid faster.'
          }, {
            icon: <BarChart3Icon className="w-6 h-6" data-id="element-952" />,
            title: 'Revenue Tracking',
            desc: "Simple, clear reporting on where your leads come from and how much money you're making."
          }, {
            icon: <Share2Icon className="w-6 h-6" data-id="element-953" />,
            title: 'Social Planner',
            desc: 'Schedule and publish posts to Google, Facebook, and Instagram from one dashboard.'
          }, {
            icon: <HistoryIcon className="w-6 h-6" data-id="element-954" />,
            title: 'Customer History',
            desc: 'Never forget a detail. See every past job, invoice, and conversation before you pick up the phone.'
          }].map((feature, i) => <ScrollReveal key={i} delay={i * 0.05} data-id="element-955">
                <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800 h-full hover:border-viridia/30 transition-colors duration-300 group" data-id="element-956">
                  <div className="w-12 h-12 rounded-lg bg-viridia/10 flex items-center justify-center text-viridia mb-4 group-hover:bg-viridia/20 transition-colors" data-id="element-957">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-958">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed" data-id="element-959">
                    {feature.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* OPERATING SYSTEM ANGLE */}
      <section className="py-24 bg-viridia-surface border-y border-gray-800 relative overflow-hidden" data-id="element-960">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 mix-blend-overlay" data-id="element-961"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-962">
          <ScrollReveal data-id="element-963">
            <div className="w-16 h-16 rounded-2xl bg-viridia/10 flex items-center justify-center mx-auto mb-6" data-id="element-964">
              <ZapIcon className="w-8 h-8 text-viridia" data-id="element-965" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-966">
              More Than Just a CRM
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed" data-id="element-967">
              Most businesses pay for a CRM, a separate calendar app, an
              invoicing tool, a text-marketing service, and a review platform.
            </p>
            <p className="text-lg text-gray-400" data-id="element-968">
              We bring all those core business functions together. You get the
              convenience and leverage of having the tools you need in one
              place, eliminating the friction of piecing together multiple
              subscriptions.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* USE CASES */}
      <section className="py-24 bg-viridia-black" data-id="element-969">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-970">
          <ScrollReveal data-id="element-971">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-972">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-973">
                Built for Local Growth
              </h2>
              <p className="text-lg text-gray-400" data-id="element-974">
                How different industries use our system to operate more
                efficiently.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-975">
            <ScrollReveal delay={0.1} data-id="element-976">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full" data-id="element-977">
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-978">
                  Home Service Companies
                </h3>
                <p className="text-viridia text-sm font-medium mb-4" data-id="element-979">
                  HVAC, Plumbers, Landscapers
                </p>
                <p className="text-gray-400 leading-relaxed" data-id="element-980">
                  Track a lead from the first Yelp message, schedule the
                  estimate on the calendar, send the quote via text, and collect
                  payment when the job is done — without ever leaving the app.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-981">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full" data-id="element-982">
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-983">
                  Health & Wellness
                </h3>
                <p className="text-viridia text-sm font-medium mb-4" data-id="element-984">
                  Med Spas, Salons, Clinics
                </p>
                <p className="text-gray-400 leading-relaxed" data-id="element-985">
                  Handle online bookings, send automated appointment reminders
                  to reduce no-shows, and manage ongoing customer communication
                  and promotional offers from one clean inbox.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3} data-id="element-986">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full" data-id="element-987">
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-988">
                  Professional Services
                </h3>
                <p className="text-viridia text-sm font-medium mb-4" data-id="element-989">
                  Law Firms, Accountants
                </p>
                <p className="text-gray-400 leading-relaxed" data-id="element-990">
                  Track new inquiries through a strict pipeline, manage
                  consultation bookings, and keep a secure, centralized record
                  of all client communication and document requests.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.4} data-id="element-991">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full" data-id="element-992">
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-993">
                  Growing Local Businesses
                </h3>
                <p className="text-viridia text-sm font-medium mb-4" data-id="element-994">
                  Retail, Restaurants, Auto
                </p>
                <p className="text-gray-400 leading-relaxed" data-id="element-995">
                  Gain instant visibility into revenue, manage customer lists
                  for holiday promotions, and keep your team aligned with a
                  single source of truth for customer history.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* SYSTEM CONNECTION */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-996">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-997">
          <ScrollReveal data-id="element-998">
            <div className="bg-viridia-black rounded-2xl p-8 md:p-12 border border-gray-800" data-id="element-999">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center" data-id="element-1000">
                Part of a Complete Growth System
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1001">
                <div className="text-center" data-id="element-1002">
                  <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-4" data-id="element-1003">
                    <TargetIcon className="w-5 h-5 text-blue-400" data-id="element-1004" />
                  </div>
                  <h4 className="text-white font-bold mb-2" data-id="element-1005">Ad Campaigns</h4>
                  <p className="text-sm text-gray-400" data-id="element-1006">Drive the traffic</p>
                </div>
                <div className="text-center" data-id="element-1007">
                  <div className="w-12 h-12 rounded-full bg-viridia/10 flex items-center justify-center mx-auto mb-4" data-id="element-1008">
                    <ZapIcon className="w-5 h-5 text-viridia" data-id="element-1009" />
                  </div>
                  <h4 className="text-white font-bold mb-2" data-id="element-1010">AI Conversion</h4>
                  <p className="text-sm text-gray-400" data-id="element-1011">
                    Captures the lead instantly
                  </p>
                </div>
                <div className="text-center" data-id="element-1012">
                  <div className="w-12 h-12 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-4" data-id="element-1013">
                    <StarIcon className="w-5 h-5 text-yellow-400" data-id="element-1014" />
                  </div>
                  <h4 className="text-white font-bold mb-2" data-id="element-1015">Reputation</h4>
                  <p className="text-sm text-gray-400" data-id="element-1016">
                    Builds trust after the job
                  </p>
                </div>
              </div>
              <div className="mt-8 pt-8 border-t border-gray-800 text-center" data-id="element-1017">
                <p className="text-gray-300" data-id="element-1018">
                  And the{' '}
                  <strong className="text-white" data-id="element-1019">CRM & Business Tools</strong>{' '}
                  sit at the center of it all, organizing the chaos so you can
                  scale without breaking.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia-dark to-viridia relative overflow-hidden" data-id="element-1020">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-1021"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-1022">
          <ScrollReveal data-id="element-1023">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-1024">
              Ready to Simplify Your Operations?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-1025">
              Track every opportunity, automate your follow-up, and build a
              cleaner system for growth.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1026">
              <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-white text-viridia-dark font-bold text-lg hover:bg-gray-100 transition-colors duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1" data-id="element-1027">
                Book a Strategy Call
                <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1028" />
              </Link>
              <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border-2 border-white text-white font-bold text-lg hover:bg-white/10 transition-colors duration-300" data-id="element-1029">
                View Packages
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}
// Add TrendingUpIcon since it was missing from imports
function TrendingUpIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1030">
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" data-id="element-1031" />
      <polyline points="16 7 22 7 22 13" data-id="element-1032" />
    </svg>;
}